import { motion } from "framer-motion";
import { Landmark, Users, MapPin } from "lucide-react";
import ChapterMark from "@/components/ChapterMark";

const PRIMARY_ROLES = ["Councillors", "Environmental officers", "Sustainability teams", "Waterway management staff"];

const PROFILE = [
    { value: "25–65", label: "Core adult audience" },
    { value: "Professional & government", label: "Typical sector and roles" },
    { value: "Moderate to high", label: "Income band" },
    { value: "All genders", label: "No restriction" },
];

const GEO_CHIPS = [
    "Sydney, NSW first",
    "Parramatta River and surrounding catchments",
    "Urban and suburban waterways, where litter pressure is greatest",
    "Then other Australian councils and waterways",
];

export default function Audience() {
    return (
        <section id="audience" className="relative overflow-hidden scroll-mt-24 py-24 md:py-32 bg-[#041222] border-y border-[#12283C]" data-testid="audience-section">
            <div
                className="absolute inset-0 bg-cover bg-center opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: "url(/images/harbour-peninsulas.webp)" }}
                aria-hidden="true"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-[#041222] via-transparent to-[#041222] pointer-events-none" aria-hidden="true" />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
                <div className="max-w-3xl">
                    <ChapterMark index="05" label="Who It's For" testid="audience-eyebrow" />
                    <h2 className="font-display font-semibold tracking-tight text-4xl md:text-5xl" data-testid="audience-heading">
                        Built for councils, open to the community
                    </h2>
                    <p className="mt-5 text-lg md:text-xl text-slate-400 leading-relaxed" data-testid="audience-intro">
                        RiverGuardian serves the people who fund and manage the waterway, and keeps the
                        community who lives beside it informed.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.55 }}
                        className="bg-[#06182A] border border-[#12283C] rounded-xl p-7 hover:border-sky-400/50 transition-colors"
                        data-testid="audience-primary-card"
                    >
                        <div className="flex items-center gap-3">
                            <span className="w-11 h-11 rounded-lg bg-sky-400/10 border border-sky-400/30 flex items-center justify-center">
                                <Landmark className="w-5 h-5 text-sky-200" />
                            </span>
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-sky-200">Primary users and purchasers</p>
                        </div>
                        <p className="mt-4 font-display font-semibold text-2xl">Councils and council decision makers</p>
                        <p className="mt-3 text-base text-slate-400 leading-relaxed">
                            The people who can use this website to monitor RiverGuardian's performance and
                            decide whether to fund or expand the service.
                        </p>
                        <div className="mt-5 flex flex-wrap gap-2" data-testid="audience-primary-roles">
                            {PRIMARY_ROLES.map((role, i) => (
                                <span
                                    key={role}
                                    className="text-sm font-medium text-sky-200 bg-sky-400/10 border border-sky-400/30 rounded-full px-4 py-1.5"
                                    data-testid={`audience-role-${i}`}
                                >
                                    {role}
                                </span>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="bg-[#05251C] border border-emerald-900/60 rounded-xl p-7 hover:border-emerald-400/50 transition-colors"
                        data-testid="audience-secondary-card"
                    >
                        <div className="flex items-center gap-3">
                            <span className="w-11 h-11 rounded-lg bg-emerald-400/10 border border-emerald-400/30 flex items-center justify-center">
                                <Users className="w-5 h-5 text-emerald-300" />
                            </span>
                            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-emerald-300">Secondary users</p>
                        </div>
                        <p className="mt-4 font-display font-semibold text-2xl">Local residents and Indigenous community members</p>
                        <p className="mt-3 text-base text-slate-300 leading-relaxed">
                            People of different ages who live near the waterways and care about environmental
                            protection and local decision-making, using the site to see the condition of their
                            waterways, the pollution data and the impact RiverGuardian is making.
                        </p>
                    </motion.div>
                </div>

                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#12283C] border border-[#12283C] rounded-xl overflow-hidden" data-testid="audience-profile">
                    {PROFILE.map((item, i) => (
                        <div key={item.label} className="bg-[#06182A] p-5" data-testid={`audience-profile-${i}`}>
                            <p className="font-display font-semibold text-xl text-sky-200">{item.value}</p>
                            <p className="mt-1 text-sm text-slate-400 leading-snug">{item.label}</p>
                        </div>
                    ))}
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55 }}
                    className="mt-6 bg-[#06182A] border border-[#12283C] rounded-xl p-7"
                    data-testid="audience-geo-card"
                >
                    <div className="flex items-center gap-3">
                        <MapPin className="w-5 h-5 text-sky-200" />
                        <p className="font-display font-semibold text-xl">Where we start, and where this goes</p>
                    </div>
                    <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="audience-geo-chips">
                        {GEO_CHIPS.map((chip, i) => (
                            <div
                                key={chip}
                                className="border-l-2 border-sky-400/60 pl-4 py-1"
                                data-testid={`audience-geo-${i}`}
                            >
                                <p className="text-sm text-slate-300 leading-relaxed">{chip}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
